import React, { useState, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Row, Col, Radio, Tooltip, Table, Tag, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { InfoCircleOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { CustomInput, CustomSelect, TextArea, PhoneInput } from "ui/Input"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import { Icon } from "ui/Icon"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { LicensesSection } from "./LicensesSection"
import { StaffDaySelector } from "./StaffDaySelector"
import { SeasonAvailability } from "./SeasonAvailability"
import styles from "./AddStaffModal.module.css"
import { createStaffProfile, getStaffProfile, updateStaffProfile, getRoles, selectRoles, selectRolesLoading } from "store/staff"
import { useProfileDetailsModals } from "hooks/useProfileDetailsModals"
import RemoveExperienceWithBookingsModal from "ui/RemoveExperienceWithBookingsModal"
import { fetchExperiences } from "store/experiences"
import { AVAILABILITY_TYPES } from "./constants"
import { transformExperiencesToOptions, transformRolesToOptions } from "./dataTransformers"
import { buildStaffPayload } from "./payloadBuilder"
import { validateStaffForm } from "./validation"
import { useStaffForm } from "./hooks/useStaffForm"
import { useStaffFormActions } from "./hooks/useStaffFormActions"
import { STAFF } from "../constants"
import { capitalizeFirstLetter } from "utils/data-formatters"
import { handleNameKeyDown, handleFirstNamePaste, handleFirstNameInput, sanitizeMultiWordTyping, sanitizeMultiWord } from "utils/InputSanitizers"
import { handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"
import IconWithBg from "ui/IconWithBg"

const AddStaffForm = ({
  open,
  onCancel,
  onRequestClose,
  markDirty,
  onSave,
  userId,
  staffId,
  editMode = false,
  isFullScreen = false,
  getProfileAction = getStaffProfile,
  updateProfileAction = updateStaffProfile,
  memberType = "staff"
}) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const roles = useSelector(selectRoles)
  const rolesLoading = useSelector(selectRolesLoading)
  const experiences = useSelector((state) => state.experiences.list)
  const experiencesLoading = useSelector((state) => state.experiences.loading)
  const [isFetchingData, setIsFetchingData] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [showAiModal, setShowAiModal] = useState(false)
  const [profileDescription, setProfileDescription] = useState("")
  const [showAllAssignedExperiences, setShowAllAssignedExperiences] = useState(false)

  const {
    formData,
    avatarUrl,
    licenses,
    licenseSectionKey,
    isLicensesExpanded,
    selectedDays,
    tripAssignments,
    availabilityIds,
    initialAvailabilityType,
    seasons,
    setFormData,
    setLicenses,
    setIsLicensesExpanded,
    setSelectedDays,
    setTripAssignments,
    setSeasons,
    populateFormWithStaffData,
    resetForm,
    handleImageChange
  } = useStaffForm()

  const { handleInputChange, handlePhoneChange } = useStaffFormActions({
    setFormData,
    setTripAssignments,
    validationErrors,
    setValidationErrors,
    t
  })

  const staffName = `${formData.firstName} ${formData.lastName}`.trim() || "Staff Member"
  const { handleProfileUpdate, assignConflictModalProps } = useProfileDetailsModals(
    staffId,
    staffName,
    updateProfileAction,
    getProfileAction
  )
  useEffect(() => {
    if (open) {
      dispatch(getRoles({ has_permissions: true }))
      dispatch(fetchExperiences(STAFF.STAFF_WINDOW))

      if (editMode && staffId) {
        setIsFetchingData(true)
        dispatch(getProfileAction(staffId))
          .then((response) => {
            const staffData = response.data || response
            populateFormWithStaffData(staffData)
          })
          .catch((error) => {
            console.error("Failed to fetch staff profile:", error)
          })
          .finally(() => {
            setIsFetchingData(false)
          })
      }
    }
  }, [open, dispatch, editMode, staffId, populateFormWithStaffData]) // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = useCallback(async () => {
    const errors = await validateStaffForm({ formData, selectedDays, seasons }, t)
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, selectedDays, seasons, t])

  const handleSave = useCallback(async () => {
    if (!(await validateForm())) {
      return
    }

    setIsSaving(true)
    try {
      const payload = buildStaffPayload({
        formData,
        userId,
        licenses,
        tripAssignments,
        selectedDays,
        seasons,
        availabilityIds,
        initialAvailabilityType,
        editMode
      })

      let result
      if (editMode && staffId) {
        result = await handleProfileUpdate(staffId, payload)
      } else {
        result = await dispatch(createStaffProfile(payload))
      }

      if (onSave) {
        onSave(result)
      }

      resetForm()
      setValidationErrors({})
      onCancel()
    } catch (error) {
      if (error?.isBookingConflict) {
        resetForm()
        setValidationErrors({})
        onCancel()
        throw error
      }
      if (error?.data?.nested_errors?.availabilities_attributes) {
        setValidationErrors((prev) => ({
          ...prev,
          availabilities_attributes: error.data.nested_errors.availabilities_attributes
        }))
        const validationError = new Error("Validation error")
        validationError.isValidationError = true
        throw validationError
      }
      console.error(`Failed to ${editMode ? "update" : "create"} staff profile:`, error)
      throw error
    } finally {
      setIsSaving(false)
    }
  }, [
    validateForm,
    formData,
    userId,
    licenses,
    tripAssignments,
    selectedDays,
    seasons,
    availabilityIds,
    initialAvailabilityType,
    editMode,
    staffId,
    handleProfileUpdate,
    dispatch,
    onSave,
    resetForm,
    onCancel
  ])

  const handleCancel = useCallback(() => {
    resetForm()
    setValidationErrors({})
    ;(onRequestClose ?? onCancel)()
  }, [resetForm, onRequestClose, onCancel])

  const handleInputChangeDirty = useCallback(
    (field, value) => {
      markDirty()
      handleInputChange(field, value)
    },
    [markDirty, handleInputChange]
  )

  const handlePhoneChangeDirty = useCallback(
    (phone, countryCode) => {
      markDirty()
      handlePhoneChange(phone, countryCode)
    },
    [markDirty, handlePhoneChange]
  )

  const handleImageChangeDirty = useCallback(
    (e) => {
      markDirty()
      handleImageChange(e)
    },
    [markDirty, handleImageChange]
  )

  const permissionRoles = useMemo(() => transformRolesToOptions(roles), [roles])

  const allExperiences = useMemo(() => transformExperiencesToOptions(experiences), [experiences])

  const experienceOptions = useMemo(
    () => allExperiences.map((e) => ({ value: e?.value, label: `${e?.label} (${capitalizeFirstLetter(e?.status)})` })),
    [allExperiences]
  )

  const assignedExperiences = useMemo(
    () => formData.experiences.map((val) => allExperiences.find((e) => e.value === val)).filter(Boolean),
    [formData.experiences, allExperiences]
  )

  const removeExperience = useCallback(
    (value) => {
      markDirty()
      handleInputChange(
        "experiences",
        formData.experiences.filter((v) => v !== value)
      )
    },
    [formData.experiences, handleInputChange, markDirty]
  )

  const columns = useMemo(
    () => [
      {
        title: t("staff.add_staff_modal.experience_name"),
        dataIndex: "label",
        key: "label",
        render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>
      },
      {
        title: t("staff.add_staff_modal.guests"),
        dataIndex: "guests",
        key: "guests",
        align: "center"
      },
      {
        title: t("staff.add_staff_modal.price"),
        dataIndex: "price",
        key: "price",
        align: "right",
        render: (p) => `$${Number(p).toFixed(2)}`
      },
      {
        title: t("staff.add_staff_modal.status"),
        dataIndex: "status",
        key: "status",
        align: "center",
        render: (status) => (
          <Tag color={status === "published" ? "green" : "default"}>
            {status === "published" && <CheckOutlined style={{ color: "var(--color-success, #16a34a)" }} />}
            <span style={{ textTransform: "capitalize" }}>{status}</span>
          </Tag>
        )
      },
      {
        title: "",
        key: "action",
        align: "right",
        render: (_, record) => (
          <Button type="text" icon={<DeleteOutlined style={{ fontSize: 18 }} />} onClick={() => removeExperience(record.value)} />
        )
      }
    ],
    [t, removeExperience]
  )

  const formContent = (
    <Spin spinning={isFetchingData} tip={"Loading Data..."}>
      <div className={isFullScreen ? styles.fullScreenContent : styles.staffModalContent}>
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>{t("staff.add_staff_modal.personal_information")}</Text>
          <Flex justify="start" className={styles.avatarSection}>
            <AddAvatar avatar={avatarUrl} handleImageChange={handleImageChangeDirty} />
          </Flex>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <div className={styles.formField}>
                <label className={styles.label}>
                  {t("staff.add_staff_modal.first_name")}
                  <span className={styles.required}>*</span>
                </label>
                <CustomInput
                  placeholder={t("staff.add_staff_modal.placeholder.name")}
                  value={formData.firstName}
                  onChange={(e) => handleInputChangeDirty("firstName", e.target.value)}
                  status={validationErrors.firstName ? "error" : ""}
                  onKeyDown={handleNameKeyDown}
                  onInput={handleFirstNameInput}
                  onPaste={(e) => handleFirstNamePaste(e, (text) => handleInputChange("firstName", text))}
                />
                {validationErrors.firstName && (
                  <Text color="var(--color-error)" size="12" className="mt-xs">
                    {validationErrors.firstName}
                  </Text>
                )}
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className={styles.formField}>
                <label className={styles.label}>
                  {t("staff.add_staff_modal.last_name")}
                  <span className={styles.required}>*</span>
                </label>
                <CustomInput
                  placeholder={t("staff.add_staff_modal.placeholder.last_name")}
                  value={formData.lastName}
                  onChange={(e) => handleInputChangeDirty("lastName", e.target.value)}
                  status={validationErrors.lastName ? "error" : ""}
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  onBlur={(e) => handleInputChange("lastName", e.target.value.trim())}
                />
                {validationErrors.lastName && (
                  <Text color="var(--color-error)" size="12" className="mt-xs">
                    {validationErrors.lastName}
                  </Text>
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <div className={styles.formField}>
                <label className={styles.label}>
                  {t("staff.add_staff_modal.email")}
                  <span className={styles.required}>*</span>
                </label>
                <CustomInput
                  placeholder={t("staff.add_staff_modal.placeholder.email")}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChangeDirty("email", e.target.value)}
                  status={validationErrors.email ? "error" : ""}
                  disabled={editMode}
                />
                {validationErrors.email && (
                  <Text color="var(--color-error)" size="12" className="mt-xs">
                    {validationErrors.email}
                  </Text>
                )}
              </div>
            </Col>

            <Col xs={24} sm={12}>
              <div className={styles.formField}>
                <label className={styles.label}>
                  {t("staff.add_staff_modal.phone_number")}
                  <span className={styles.required}>*</span>
                </label>
                <PhoneInput
                  initialValue={formData.phone}
                  initialCountryCode={formData.phoneCountryCode}
                  onChange={handlePhoneChangeDirty}
                  status={validationErrors.phone ? "error" : ""}
                />
                {validationErrors.phone && (
                  <Text color="var(--color-error)" size="12" className="mt-xs">
                    {validationErrors.phone}
                  </Text>
                )}
              </div>
            </Col>
          </Row>

          <div className={styles.formField}>
            <label className={styles.label}>{t("staff.add_staff_modal.title_label")}</label>
            <CustomInput
              placeholder={t("staff.add_staff_modal.placeholder.title")}
              value={formData.title}
              onChange={(e) => handleInputChangeDirty("title", sanitizeMultiWordTyping(e.target.value))}
              onBlur={(e) => handleInputChangeDirty("title", sanitizeMultiWord(e.target.value))}
              onPaste={(e) => {
                e.preventDefault()
                handleInputChangeDirty("title", sanitizeMultiWord(e.clipboardData.getData("text")))
              }}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.label}>
              {t("staff.add_staff_modal.permission_role")}
              <span className={styles.required}>*</span>
            </label>
            <CustomSelect
              placeholder={t("staff.add_staff_modal.placeholder.select_role")}
              options={permissionRoles}
              value={formData.permissionRole}
              onChange={(value) => handleInputChangeDirty("permissionRole", value)}
              style={{ width: "100%" }}
              status={validationErrors.permissionRole ? "error" : ""}
              loading={rolesLoading}
            />
            {validationErrors.permissionRole && (
              <Text color="var(--color-error)" size="12" className="mt-xs">
                {validationErrors.permissionRole}
              </Text>
            )}
          </div>
          <div className={styles.formField}>
            <Flex justify="space-between" align="center" className="mb-xs" wrap gap={8}>
              <label className={styles.label}>
                {t("staff.add_staff_modal.description")}
                <span className={styles.required}>*</span>
              </label>
              <Button
                type="primary"
                icon={
                  <Icon
                    name="aiEnhancement"
                    color={formData.description.length < 25 ? colors.textPrimary : colors.disabled}
                    height={20}
                    width={20}
                  />
                }
                onClick={() => setShowAiModal(true)}
                disabled={formData.description.length < 25}
              >
                {t("client.enhance_with_ui")}
              </Button>
            </Flex>
            <TextArea
              placeholder={t("staff.add_staff_modal.description_placeholder")}
              value={formData.description}
              onChange={(e) => handleInputChangeDirty("description", e.target.value)}
              style={{ height: 86 }}
              status={validationErrors.description ? "error" : ""}
            />
            {validationErrors.description && (
              <Text color="var(--color-error)" size="12" className="mt-xs">
                {validationErrors.description}
              </Text>
            )}
          </div>
        </div>
        <div className={styles.section}>
          <LicensesSection
            key={licenseSectionKey}
            isExpanded={isLicensesExpanded}
            onToggle={() => setIsLicensesExpanded(!isLicensesExpanded)}
            licenses={licenses}
            onLicensesChange={(v) => {
              markDirty()
              setLicenses(v)
            }}
          />
        </div>
        <div className={styles.section}>
          <Flex align="center" gap={6} className="mb-m">
            <Text className={styles.sectionTitle}>{t("staff.add_staff_modal.availability")}</Text>
            <Tooltip title={t("staff.add_staff_modal.availability_tooltip")}>
              <InfoCircleOutlined style={{ fontSize: 14, color: "var(--color-primary)", marginBottom: "24px" }} />
            </Tooltip>
          </Flex>
          <div className={styles.formField}>
            <Radio.Group
              value={formData.availability}
              onChange={(e) => handleInputChangeDirty("availability", e.target.value)}
              className={styles.radioGroup}
            >
              <Radio value={AVAILABILITY_TYPES.ALL_YEAR}>{t("staff.add_staff_modal.all_year")}</Radio>
              <Radio value={AVAILABILITY_TYPES.SEASONAL}>
                <Flex align="center" gap={6}>
                  {t("staff.add_staff_modal.seasonal")}
                  <Tooltip title={t("staff.add_staff_modal.seasonal_tooltip")}>
                    <InfoCircleOutlined style={{ fontSize: 14, color: "var(--color-primary)" }} />
                  </Tooltip>
                </Flex>
              </Radio>
            </Radio.Group>
          </div>
          {formData?.availability === AVAILABILITY_TYPES.SEASONAL ? (
            <>
              <SeasonAvailability
                seasons={seasons}
                setSeasons={(newSeasons) => {
                  markDirty()
                  setSeasons(newSeasons)
                  if (validationErrors.seasons) {
                    setValidationErrors((prev) => ({ ...prev, seasons: undefined }))
                  }
                  if (validationErrors.availabilities_attributes) {
                    setValidationErrors((prev) => ({ ...prev, availabilities_attributes: undefined }))
                  }
                }}
                validationErrors={validationErrors.availabilities_attributes || {}}
                memberType={memberType}
              />
              {validationErrors.seasons && (
                <Text color="var(--color-error)" size="12" className="mt-xs" style={{ display: "block" }}>
                  {validationErrors.seasons}
                </Text>
              )}
            </>
          ) : (
            <div className={styles.formField}>
              <label className={styles.label}>{t("staff.add_staff_modal.days_times_question", { memberType })}</label>
              <StaffDaySelector
                selectedDays={selectedDays}
                setSelectedDays={(days) => {
                  markDirty()
                  setSelectedDays(days)
                  if (validationErrors?.selectedDays) {
                    setValidationErrors((prev) => ({ ...prev, selectedDays: undefined }))
                  }
                }}
              />
              {validationErrors?.selectedDays && (
                <Text color="var(--color-error)" size="12" className="mt-xs">
                  {validationErrors?.selectedDays}
                </Text>
              )}
            </div>
          )}
        </div>
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>{t("staff.add_staff_modal.assign_experiences")}</Text>
          <div className={styles.formField}>
            <label className={styles.label}>{t("staff.add_staff_modal.experience")}</label>
            <div className={styles.customSelectWrapper}>
              <CustomSelect
                mode="multiple"
                placeholder={t("staff.add_staff_modal.select_experience")}
                options={experienceOptions}
                value={formData.experiences}
                onChange={(value) => handleInputChangeDirty("experiences", value)}
                style={{ width: "100%" }}
                tagRender={() => null}
                status={validationErrors?.experiences ? "error" : ""}
                loading={experiencesLoading}
              />
              <span className={styles.customPlaceholder}>{t("staff.add_staff_modal.select_experience")}</span>
            </div>
            {validationErrors?.experiences && (
              <Text color="var(--color-error)" size="12" className="mt-xs" style={{ display: "block" }}>
                {validationErrors.experiences}
              </Text>
            )}
            <div style={{ marginTop: 16 }}>
              <div className={styles.tableSectionTitle}>{t("staff.add_staff_modal.assigned_experiences")} </div>
              <div className={styles.experiencesTableWrapper}>
                <Table
                  className={styles.experiencesTable}
                  dataSource={assignedExperiences?.map((a) => ({ ...a, key: a.value }))}
                  columns={columns}
                  pagination={false}
                  rowKey="value"
                  bordered={false}
                  scroll={{ x: "max-content" }}
                />
              </div>
              <div className={styles.mobileAssignedExperiencesContainer}>
                {(showAllAssignedExperiences ? assignedExperiences : assignedExperiences.slice(0, 3)).map((experience) => {
                  const isPublished = experience.status === "published"
                  const displayStatus = experience.status === "published" ? "Published" : capitalizeFirstLetter(experience.status)

                  return (
                    <div key={experience.value} className={styles.mobileExperienceCard}>
                      <Flex justify="space-between" align="center" className={styles.mobileExperienceCardRow}>
                        <Flex vertical align="flex-start" gap={5} className={styles.mobileExperienceCardLeft}>
                          <Tag color={isPublished ? "green" : "default"}>
                            {isPublished && <CheckOutlined style={{ color: "var(--color-success, #16a34a)" }} />}
                            <span style={{ textTransform: "capitalize" }}>{displayStatus}</span>
                          </Tag>
                          <Text className={styles.mobileExperienceName}>{experience.label}</Text>
                          <Text type="secondary" className={styles.mobileExperienceCardDetails}>
                            {experience.guests} {t("staff.add_staff_modal.guests")} - ${Number(experience.price).toFixed(2)}
                          </Text>
                        </Flex>

                        <IconWithBg
                          className={styles.deleteIcon}
                          height={18}
                          width={18}
                          icon={<DeleteOutlined fill="#ff0000" />}
                          onClick={() => removeExperience(experience.value)}
                        />
                      </Flex>
                    </div>
                  )
                })}

                {assignedExperiences.length > 3 && (
                  <div className={styles.mobileExperienceSeeMore}>
                    <Button type="link" onClick={() => setShowAllAssignedExperiences((prev) => !prev)}>
                      {showAllAssignedExperiences
                        ? t("equipment.details.see_less", { defaultValue: "See less" })
                        : t("equipment.details.see_more", { defaultValue: "See more" })}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Flex gap={12} justify="flex-start" className={isFullScreen ? styles.fullScreenFooter : styles.footer}>
          <Button size="large" onClick={handleCancel} disabled={isSaving}>
            {t("staff.add_staff_modal.cancel")}
          </Button>
          <Button type="primary" size="large" onClick={handleSave} loading={isSaving}>
            {editMode ? t("staff.edit_staff_modal.save") : t("staff.add_staff_modal.save")}
          </Button>
        </Flex>
      </div>
    </Spin>
  )

  return (
    <>
      {formContent}
      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription("")
          }}
          profileDescription={profileDescription}
          description={formData.description}
          setDescription={(value) => handleInputChange("description", value)}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setShowAiModal(false)
          }}
          text={t("pro.ai_enhancement.form.textarea_label")}
          modalTitle={t("pro.ai_enhancement.modal_title")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("pro.ai_enhancement.form.ai_info")}
        />
      )}
      {editMode && <RemoveExperienceWithBookingsModal {...assignConflictModalProps} />}
    </>
  )
}

AddStaffForm.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func,
  markDirty: PropTypes.func,
  onSave: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  staffId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editMode: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  getProfileAction: PropTypes.func,
  updateProfileAction: PropTypes.func,
  memberType: PropTypes.string
}

export default AddStaffForm
